import { useEffect, useMemo, useState } from 'react';
import { Button, ConfigProvider, DatePicker, Drawer, Input, Select } from 'antd';
import heIL from 'antd/locale/he_IL';
import dayjs, { type Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { Money } from '../../components/Money';
import { fetchStaffMembers } from '../../api/staffMembers';
import { useCreateBankDeposit } from '../../hooks/useCashFlow';
import { useLocationContext } from '../../auth/LocationContext';
import { useProjectContext } from '../../auth/ProjectContext';
import { todayIsrael } from '../../utils/dates';
import type { ClosingSummary } from '@deposits/shared';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (depositId: number) => void;
  /** in_safe envelopes the user selected in the safe table. */
  selected: ClosingSummary[];
}

export function NewBankDepositDrawer({ open, onClose, onCreated, selected }: Props) {
  const { selectedLocationId, selectedLocation } = useLocationContext();
  const { selectedProjectId, selectedProject } = useProjectContext();

  const [depositDate, setDepositDate] = useState<Dayjs>(() => dayjs(todayIsrael()));
  const [depositorId, setDepositorId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open) {
      setDepositDate(dayjs(todayIsrael()));
      setDepositorId(null);
      setNotes('');
    }
  }, [open]);

  const staffQuery = useQuery({
    queryKey: ['staff-members'],
    queryFn: fetchStaffMembers,
    staleTime: 5 * 60_000,
  });

  const total = useMemo(
    () => selected.reduce((s, c) => s + Number(c.amount_to_safe), 0),
    [selected],
  );

  const createMutation = useCreateBankDeposit();

  const submit = () => {
    if (selected.length === 0 || selectedLocationId == null) return;
    createMutation.mutate(
      {
        location_id: selectedLocationId,
        project_id: selectedProjectId ?? undefined,
        deposit_date: depositDate.format('YYYY-MM-DD'),
        depositor_arbox_users_boxes_id: depositorId,
        notes: notes.trim() || null,
        closing_ids: selected.map((c) => c.id),
      },
      {
        onSuccess: (created) => {
          toast.success(`הפקדה לבנק מספר ${created.deposit_number} נוצרה`);
          if (!created.pdf_url) toast.error('ההפקדה נוצרה אך נכשלה הפקת ה-PDF');
          onCreated(created.id);
        },
        onError: (err) => {
          toast.error(err.message || 'שגיאה ביצירת ההפקדה');
          if (err.message?.includes('אינן זמינות')) onClose();
        },
      },
    );
  };

  return (
    <Drawer
      title={<span className="text-heading-3-bold text-main-text">הפקדה לבנק מהכספת</span>}
      placement="left"
      open={open}
      onClose={onClose}
      width={560}
      closable={false}
      styles={{ header: { borderBottom: 'none' } }}
    >
      <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
        <div className="bg-blue-bg border border-border rounded p-4 mb-6">
          <div className="font-semibold mb-3">סיכום הבחירה</div>
          <div className="grid grid-cols-2 gap-y-2 gap-x-3 text-base">
            <SummaryRow label="סניף"><span>{selectedLocation?.name ?? '—'}</span></SummaryRow>
            {selectedProject && (
              <SummaryRow label="פרויקט"><span>{selectedProject.name}</span></SummaryRow>
            )}
            <SummaryRow label="מספר סגירות"><span dir="ltr">{selected.length}</span></SummaryRow>
            <SummaryRow label='סה"כ להפקדה' bold><Money amount={total} /></SummaryRow>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <Field label="תאריך הפקדה" required>
            <DatePicker
              value={depositDate}
              onChange={(d) => d && setDepositDate(d)}
              format="DD/MM/YYYY"
              allowClear={false}
              style={{ width: '100%' }}
            />
          </Field>

          <Field label="איש צוות מפקיד">
            <Select
              value={depositorId ?? undefined}
              onChange={(v) => setDepositorId(v ?? null)}
              placeholder="בחר איש צוות (לא חובה)"
              allowClear
              showSearch
              optionFilterProp="label"
              loading={staffQuery.isLoading}
              options={(staffQuery.data ?? [])
                .slice()
                .sort((a, b) => a.display_name.localeCompare(b.display_name, 'he'))
                .map((u) => ({ value: u.arbox_users_boxes_id, label: u.display_name }))}
              style={{ width: '100%' }}
            />
          </Field>

          <Field label="הערות">
            <Input.TextArea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="פירוט אופציונלי..."
            />
          </Field>

          <div className="flex justify-end gap-3 mt-2">
            <Button onClick={onClose}>ביטול</Button>
            <Button
              type="primary"
              onClick={submit}
              loading={createMutation.isPending}
              disabled={selected.length === 0}
            >
              הפק
            </Button>
          </div>
        </div>
      </ConfigProvider>
    </Drawer>
  );
}

function SummaryRow({ label, children, bold }: { label: string; children: React.ReactNode; bold?: boolean }) {
  return (
    <>
      <div className="text-secondary-text">{label}</div>
      <div className={`text-end ${bold ? 'font-semibold text-heading-3' : ''}`}>{children}</div>
    </>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-small text-main-text mb-1">
        {label}
        {required && <span className="text-error-red"> *</span>}
      </div>
      {children}
    </div>
  );
}
